<template>
  <div class="product-container" :class="divClass">
    <div class="product-header">
      <span class="product-title">{{ props.product.titulo }}</span>
      <span class="status-tag" :class="statusTagClass">{{ productStatus }}</span>
    </div>
    <span class="product-amounts">Comprados: {{ props.product.cantidad }} | Canjeados: {{ props.product.cantidad_canjeada }}</span>
    
    <div v-if="editing" class="edit-form">
        <input
          type="number"
          v-model="newQuantity"
          placeholder="Nueva cantidad"
          @input="validateInteger"
          class="standard-input"
        />
        <select 
          v-model="newProduct"
          v-if="purchaseData.other_products_in_plan"
          class="standard-select"
          :class="{ 'placeholder-color': newProduct === '' }">
          <option value="">Selecciona un producto</option>
          <option 
            v-for="item in purchaseData.other_products_in_plan"
            :key="item.id_producto_promo"
            :value="item.id_producto_promo">
            {{ item.titulo }} {{ formatFechaHora(item.fecha, item.hora) }}
          </option>
        </select>
      <div class="buttons-container">
        <button @click="cancelEditing()" class="secondary_button">Cancelar</button>
        <button @click="saveEditProduct()" class="main_button">Guardar</button>
      </div>

    </div>
    <div v-if="!editing" class="buttons-container">
      <button @click="deleteUserInProduct()" class="main_button danger">Eliminar</button>
      <button @click="editProduct()" class="main_button">Cambiar producto</button>
    </div>
  </div>
  <TwoButtonPopup 
    :title="twoButtonTitle"
    :message="twoButtonMessage"
    :acceptButton="twoButtonAcceptButton"
    @acceptCallback="twoButtonConfirmedCallback"
    :cancelButton="twoButtonCancelButton"
    @cancelCallback="twoButtonCancelCallback"
    />
  <ErrorPopup :errorMessage="error" />

</template>
  
<script setup>
  import { defineProps, ref, computed, defineEmits } from 'vue';
  import TwoButtonPopup from '@/components/common/TwoButtonPopup.vue';
  import ErrorPopup from "@/components/common/ErrorPopup.vue";
  import { useLoadingStore } from "@/stores/loadingStore";
  import { refreshToken } from "@/services/authService";
  import { useRouter } from 'vue-router';

  const props = defineProps({
    status: String,
    product: Object,
    purchaseData: Object,
  });
  
  const emit = defineEmits(['userProductsChanged']);

  const editing = ref(false);
  const newQuantity = ref("");
  const newProduct = ref("");
  const productStatus = ref("");
  const loadingStore = useLoadingStore();
  const router = useRouter();
  const error = ref(null);

  if (props.product == null) {
    productStatus.value = "";
  } else if (props.product.cantidad == props.product.cantidad_canjeada) {
    productStatus.value = "Canjeado";
  } else if (props.product.cantidad_canjeada > 0) {
    productStatus.value = "Canjeado parcial.";
  } else if (props.purchaseData.purchase.estado === 'Pendiente') {
    productStatus.value = "Vigente";
  } else if (props.purchaseData.purchase.estado === 'Caducado') {
    productStatus.value = "Caducado";
  } else {
    productStatus.value = "";
  }

  const divClass = computed(() => {
    if (props.product.cantidad == props.product.cantidad_canjeada){
      return "redeemed";
    } else if (props.purchaseData.purchase.estado === 'Pendiente'){
      return "pending";
    } else { //if (props.purchaseData.purchase.estado === 'Caducado'){
      return "expired";
    }
  });

  const statusTagClass = computed(() => {
    if (props.product.cantidad == props.product.cantidad_canjeada){
      return "status-tag-redeemed";
    } else if (props.product.cantidad_canjeada > 0) {
      return "status-tag-partially-redeemed";
    } else if (props.purchaseData.purchase.estado === 'Pendiente'){
      return "status-tag-pending";
    } else { //if (props.purchaseData.purchase.estado === 'Caducado'){
      return "status-tag-expired";
    }
  })

  const formatFechaHora = (fecha, hora) => {
    if (!fecha) return '';

    const meses = [
      'ene', 'feb', 'mar', 'abr', 'may', 'jun',
      'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
    ];

    const [anio, mes, dia] = fecha.split('-');
    const nombreMes = meses[parseInt(mes) - 1];

    return `(${parseInt(dia)} ${nombreMes} ${anio}${hora ? ' a las ' + hora : ''})`;
  };

  const cancelEditing = () => {
    editing.value = false;
    newQuantity.value = "";
    newProduct.value = "";
  };

  const editProduct = () => {
    if (props.product.cantidad != props.product.cantidad_canjeada && props.purchaseData.purchase.estado === 'Pendiente') {
      editing.value = true;
    } 
  };

  function validateInteger(event) {
    const rawValue = event.target.value
    const parsed = parseInt(rawValue)

    if (!Number.isNaN(parsed) && parsed >= 0) {
      newQuantity.value = parsed
    } else {
      newQuantity.value = ''
    }
  }

  const deleteUserInProduct = () => {
    if (props.product.cantidad != props.product.cantidad_canjeada && props.purchaseData.purchase.estado === 'Pendiente') {
      twoButtonTitle.value = "Eliminar usuario del producto";
      twoButtonMessage.value = "¿Estás seguro de que quieres eliminar al usuario de este producto?";
      twoButtonAcceptButton.value = "Eliminar";
      twoButtonCancelButton.value = "Cancelar";
    } 
  };

  const saveEditProduct = async () => {
    loadingStore.isLoading = true;
    error.value = null;

      try {
      let response = await saveEditProductInAPI();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await saveEditProductInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        error.value = "Se ha producido un error cambiando el producto.";
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
      } else {
        emit('userProductsChanged');
        newQuantity.value = "";
        newProduct.value = "";
        editing.value = false;
      }
    } catch (exception) {
      error.value = exception.message;
    } finally {
      loadingStore.setLoading(false);
    }
  };

  const twoButtonTitle = ref("");
  const twoButtonMessage = ref("");
  const twoButtonAcceptButton = ref("");
  const twoButtonCancelButton = ref("");

  const twoButtonCancelCallback = () => {
    twoButtonTitle.value = "";
    twoButtonMessage.value = "";
  };

  const twoButtonConfirmedCallback = async () => {
    loadingStore.isLoading = true;
    error.value = null;

    try {
      let response = await deleteUserInProductInAPI();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await deleteUserInProductInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        error.value = "Se ha producido un error eliminando el producto.";
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
      } else {
        emit('userProductsChanged');
        twoButtonTitle.value = "";
        twoButtonMessage.value = "";
      }
    } catch (exception) {
      error.value = exception.message;
    } finally {
      loadingStore.setLoading(false);
    }
  };

  async function deleteUserInProductInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/reservations/delete_user_from_product", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "promo_user_id": props.purchaseData.user.id,
        "purchase_code": props.purchaseData.purchase.codigo,
        "product_id": props.product.id_usuario_producto,
      }),
    });
  }

  async function saveEditProductInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/reservations/edit_product_in_promo", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "promo_user_id": props.purchaseData.user.id,
        "purchase_code": props.purchaseData.purchase.codigo,
        "old_product_id": props.product.id_producto,
        "new_product_id": newProduct.value,
        "new_product_quantity": newQuantity.value
      }),
    });
    
  }
</script>
  
<style scoped>  

.product-container {
  border-radius: var(--radius-md);
  padding: 16px;
  margin-top: 15px;
  position: relative;
  border: 1px solid var(--light-gray);
  background-color: var(--white);
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.product-title {
  font-weight: 700;
  font-size: 16px;
  color: var(--text-color-dark);
  margin-right: 120px; /* Space for tag */
}

.product-amounts {
  display: block;
  font-size: 14px;
  color: var(--text-color-light);
  margin-bottom: 12px;
}

/* Status variants for container border/bg */
.pending {
  border-left: 4px solid var(--primary-color);
}

.redeemed {
  opacity: 0.7;
  background-color: var(--almost-white);
}

.expired {
  opacity: 0.7;
  background-color: var(--almost-white);
  border-left: 4px solid var(--error-color, #F15A58);
}

.status-tag {
  position: absolute;
  top: 16px;
  right: 16px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  padding: 4px 10px;
  min-width: 80px;
  text-align: center;
}

.status-tag-pending {
  background-color: var(--success-light, #e6fffa);
  color: var(--success-dark, #2c7a7b);
}

.status-tag-redeemed {
  background-color: var(--gray);
  color: white;
}

.status-tag-partially-redeemed {
  background-color: #F5BA5D;
  color: white;
}

.status-tag-expired {
  background-color: var(--error-light, #fed7d7);
  color: var(--error-dark, #c53030);
}

.buttons-container {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.edit-form {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.standard-input, .standard-select {
  width: 100%;
  box-sizing: border-box;
  padding: 10px;
  border: 1px solid var(--gray);
  border-radius: var(--radius-md);
  font-family: inherit;
  font-size: 14px;
  height: 42px; /* Consistent height */
  transition: border-color var(--transition-fast);
}

.standard-input:focus, .standard-select:focus {
  outline: none;
  border-color: var(--primary-color);
}

.placeholder-color {
  color: var(--text-color-placeholder, #a0aec0);
}
.main_button.danger {
  background-color: var(--error-color, #F15A58);
}
.main_button.danger:hover {
  background-color: #d14947;
}
</style>
  